import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, Switch } from '@headlessui/react';
import { CheckCircleIcon, PencilIcon, TrashIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useApiClient } from '../lib/api';
import StatusPill from '../components/common/StatusPill';
import OperatingHoursModal from '../components/common/OperatingHoursModal';
import Pagination from '../components/common/Pagination';
import SortableTh from '../components/common/SortableTh';
import ContextHelpButton from '../components/help/ContextHelpButton';
import ContextHelpDrawer from '../components/help/ContextHelpDrawer';
import { RESTAURANTS_HELP_SECTIONS } from '../components/help/waiterCallHelpContent';
import { useConfig } from '../providers/config-provider';
import { useAuth } from '../providers/auth-provider';
import { useNotification } from '../providers/notification-provider';
import { UI_EVENTS } from '../config';

const schema = z.object({
  name: z.string().min(2, 'Informe o nome'),
  code: z.string().optional(),
  timezone: z.string().optional(),
  isActive: z.boolean().optional(),
  isOperatingHoursEnabled: z.boolean().optional()
});

const pageSize = 10;

const dayLabelByValue = {
  0: 'Domingo',
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado'
};

const dayNameToValue = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
};

const toTimeLabel = (value) => String(value || '').slice(0, 5);

const RestaurantsPage = () => {
  const api = useApiClient();
  const config = useConfig();
  const { user } = useAuth();
  const { showToast, showConfirm } = useNotification();

  const canManageRestaurants = user?.role === 'Admin' || user?.role === 'AdminMaster' || user?.isAdminMaster;

  const [restaurants, setRestaurants] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [viewingRestaurant, setViewingRestaurant] = useState(null);
  const [viewHoursLoading, setViewHoursLoading] = useState(false);
  const [viewHoursError, setViewHoursError] = useState(null);
  const [viewOperatingHours, setViewOperatingHours] = useState([]);
  const [viewClosureDates, setViewClosureDates] = useState([]);

  const [companyProfile, setCompanyProfile] = useState(null);
  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpSectionKey, setHelpSectionKey] = useState(RESTAURANTS_HELP_SECTIONS[0].key);
  const refreshRequestIdRef = useRef(0);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      code: '',
      timezone: 'America/Sao_Paulo',
      isActive: true,
      isOperatingHoursEnabled: false
    }
  });

  const loadProfile = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/company/profile');
      setCompanyProfile(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [api]);

  const refresh = useCallback(async () => {
    const requestId = ++refreshRequestIdRef.current;

    if (!config.companyId) {
      setRestaurants([]);
      setTotalItems(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/api/v1/restaurants', {
        params: {
          companyId: config.companyId,
          includeInactive,
          page,
          pageSize,
          search: searchTerm.trim() || undefined
        }
      });

      if (requestId !== refreshRequestIdRef.current) {
        return;
      }

      const payload = response.data;
      if (payload?.items) {
        setRestaurants(payload.items);
        setTotalItems(payload.totalItems ?? 0);
        setTotalPages(Math.max(1, payload.totalPages ?? 1));
      } else {
        const items = payload || [];
        setRestaurants(items);
        setTotalItems(items.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error(error);
      if (requestId === refreshRequestIdRef.current) {
        showToast('Erro ao carregar restaurantes.', 'error');
      }
    } finally {
      if (requestId === refreshRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [api, config.companyId, includeInactive, page, searchTerm, showToast]);

  useEffect(() => {
    document.title = 'Restaurantes | Orion WaiterCall';
    refresh();
    loadProfile();
  }, [refresh, loadProfile, config.companyId]);

  const openCreate = () => {
    setModalMode('create');
    setEditingId(null);
    reset({ name: '', code: '', timezone: 'America/Sao_Paulo', isActive: true, isOperatingHoursEnabled: false });
    setModalOpen(true);
  };

  const openEdit = (restaurant) => {
    setModalMode('edit');
    setEditingId(restaurant.id);
    reset({
      name: restaurant.name,
      code: restaurant.code || '',
      timezone: restaurant.timezone,
      isActive: restaurant.isActive,
      isOperatingHoursEnabled: restaurant.isOperatingHoursEnabled
    });
    setModalOpen(true);
  };

  const openView = async (restaurant) => {
    setViewingRestaurant(restaurant);
    setViewHoursError(null);
    setViewOperatingHours([]);
    setViewClosureDates([]);

    if (!restaurant?.isOperatingHoursEnabled) {
      return;
    }

    setViewHoursLoading(true);
    try {
      const response = await api.get(`/api/v1/restaurants/${restaurant.id}/operating-hours`, {
        params: { companyId: config.companyId || undefined }
      });
      const payload = response.data || { hours: [], closureDates: [] };
      setViewOperatingHours(payload.hours || []);
      setViewClosureDates(payload.closureDates || []);
    } catch (error) {
      console.error(error);
      setViewHoursError('Não foi possível carregar os horários de funcionamento.');
    } finally {
      setViewHoursLoading(false);
    }
  };

  const openHours = (restaurant) => {
    setSelectedRestaurantId(restaurant.id);
    setHoursModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
  };

  const onSubmit = async (values) => {
    const editingRestaurant = modalMode === 'edit' ? restaurants.find((item) => item.id === editingId) : null;
    const willInactivate = modalMode === 'edit' && editingRestaurant?.isActive && values.isActive === false;
    if (willInactivate) {
      const confirmed = await showConfirm({
        title: 'Inativar restaurante',
        message: `Deseja inativar o restaurante ${editingRestaurant.name}?`,
        confirmText: 'Inativar',
        cancelText: 'Cancelar',
        type: 'warning'
      });

      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: values.name,
        code: values.code || null,
        timezone: values.timezone || 'America/Sao_Paulo',
        isActive: values.isActive ?? true,
        isOperatingHoursEnabled: values.isOperatingHoursEnabled ?? false
      };

      if (modalMode === 'edit' && editingId) {
        await api.put(`/api/v1/restaurants/${editingId}`, {
          ...payload,
          companyId: config.companyId || undefined
        });
        showToast('Restaurante atualizado com sucesso.', 'success');
      } else {
        await api.post('/api/v1/restaurants', {
          ...payload,
          companyId: config.companyId || undefined
        });
        showToast('Restaurante cadastrado com sucesso.', 'success');
      }

      closeModal();
      refresh();
      loadProfile();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new window.CustomEvent(UI_EVENTS.scopeRefresh));
      }
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || 'Não foi possível salvar o restaurante.';
      showToast(message, 'error');
      setSaving(false);
    }
  };

  const archive = async (restaurant) => {
    const confirmed = await showConfirm({
      title: 'Excluir restaurante',
      message: `Tem certeza que deseja excluir ${restaurant.name}?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar'
    });

    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      await api.delete(`/api/v1/restaurants/${restaurant.id}`, {
        params: { companyId: config.companyId || undefined }
      });
      showToast('Restaurante excluído com sucesso.', 'success');
      refresh();
      loadProfile();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new window.CustomEvent(UI_EVENTS.scopeRefresh));
      }
    } catch (error) {
      console.error(error);
      showToast('Não foi possível excluir o restaurante.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const markDefault = async (restaurant) => {
    setSaving(true);
    try {
      await api.put('/api/v1/company/profile', { defaultRestaurantId: restaurant.id });
      showToast(`${restaurant.name} agora é o restaurante padrão.`, 'success');
      loadProfile();
    } catch (error) {
      console.error(error);
      showToast('Não foi possível atualizar o restaurante padrão.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const isDefault = (id) => companyProfile?.defaultRestaurantId === id;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleSort = useCallback((key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const visibleRestaurants = useMemo(() => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const defaultRestaurantId = companyProfile?.defaultRestaurantId;
    const toText = (value) => String(value || '').trim().toLocaleLowerCase('pt-BR');

    return [...restaurants].sort((a, b) => {
      if (sortConfig.key === 'isActive') {
        if (a.isActive !== b.isActive) {
          return (a.isActive ? -1 : 1) * direction;
        }
        return toText(a.name).localeCompare(toText(b.name), 'pt-BR');
      }

      if (sortConfig.key === 'isDefault') {
        const left = a.id === defaultRestaurantId ? 0 : 1;
        const right = b.id === defaultRestaurantId ? 0 : 1;
        if (left !== right) {
          return (left - right) * direction;
        }
        return toText(a.name).localeCompare(toText(b.name), 'pt-BR');
      }

      const comparison = toText(a[sortConfig.key]).localeCompare(toText(b[sortConfig.key]), 'pt-BR') * direction;
      if (comparison !== 0) {
        return comparison;
      }

      return String(a.id || '').localeCompare(String(b.id || ''));
    });
  }, [restaurants, sortConfig, companyProfile?.defaultRestaurantId]);

  const groupedViewHours = useMemo(() => {
    const groups = new Map(Object.keys(dayLabelByValue).map((day) => [Number(day), []]));

    (viewOperatingHours || []).forEach((hour) => {
      const dayValue = typeof hour.dayOfWeek === 'number'
        ? hour.dayOfWeek
        : (dayNameToValue[hour.dayOfWeek] ?? Number(hour.dayOfWeek));

      if (Number.isNaN(dayValue) || dayValue < 0 || dayValue > 6) {
        return;
      }

      const list = groups.get(dayValue) || [];
      list.push({
        openTime: toTimeLabel(hour.openTime),
        closeTime: toTimeLabel(hour.closeTime)
      });
      groups.set(dayValue, list);
    });

    return Array.from(groups.entries())
      .map(([day, shifts]) => ({
        day,
        label: dayLabelByValue[day],
        shifts: [...shifts].sort((a, b) => a.openTime.localeCompare(b.openTime))
      }));
  }, [viewOperatingHours]);

  return (
    <section>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Restaurantes</h1>
          <p className="mt-2 text-sm text-gray-700">Gerencie unidades, horários e restaurante padrão da empresa.</p>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2 sm:ml-16 sm:mt-0 sm:flex-none">
          {canManageRestaurants && (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex flex-1 items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:flex-none"
            >
              Novo restaurante
            </button>
          )}
          <ContextHelpButton
            label="Ajuda sobre restaurantes"
            onClick={() => {
              setHelpSectionKey('registry');
              setIsHelpOpen(true);
            }}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-center gap-4">
        <input
          type="text"
          placeholder="Buscar por nome, código ou fuso..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 max-w-md"
        />
        <div className="w-full sm:w-auto">
          <div className="flex h-[38px] items-center gap-3">
            <Switch
              checked={!includeInactive}
              onChange={(checked) => {
                setIncludeInactive(!checked);
                setPage(1);
              }}
              className={`${!includeInactive ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
            >
              <span className="sr-only">Apenas ativos</span>
              <span
                aria-hidden="true"
                className={`${!includeInactive ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
            <span className="text-sm text-gray-600">Apenas ativos</span>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:hidden space-y-3">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">Carregando restaurantes...</div>
        ) : visibleRestaurants.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">Nenhum restaurante encontrado.</div>
        ) : (
          visibleRestaurants.map((restaurant) => (
            <article key={restaurant.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{restaurant.name}</h3>
                  <p className="text-xs text-gray-600">Código: {restaurant.code || 'N/D'}</p>
                  <p className="text-xs text-gray-500">Fuso: {restaurant.timezone}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {isDefault(restaurant.id) ? <StatusPill label="Padrão" variant="info" /> : null}
                  <StatusPill label={restaurant.isActive ? 'Ativo' : 'Inativo'} variant={restaurant.isActive ? 'success' : 'muted'} />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => openView(restaurant)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Ver</button>
                {canManageRestaurants && (
                  <button type="button" onClick={() => openEdit(restaurant)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Editar</button>
                )}
                {restaurant.isOperatingHoursEnabled && (
                  <button type="button" onClick={() => openHours(restaurant)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Horários</button>
                )}
                {canManageRestaurants && (
                  <button type="button" onClick={() => markDefault(restaurant)} disabled={isDefault(restaurant.id)} className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60">{isDefault(restaurant.id) ? 'Padrão' : 'Definir padrão'}</button>
                )}
                {canManageRestaurants && (
                  <button type="button" onClick={() => archive(restaurant)} disabled={saving} className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"><TrashIcon className="h-4 w-4" />Excluir</button>
                )}
              </div>
            </article>
          ))
        )}
      </div>

      <div className="mt-8 hidden sm:block">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {loading ? (
                <div className="bg-white p-4 text-sm text-gray-600">Carregando restaurantes...</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableTh
                        label="Nome"
                        sortKey="name"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      />
                      <SortableTh
                        label="Código"
                        sortKey="code"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      />
                      <SortableTh
                        label="Fuso"
                        sortKey="timezone"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      />
                      <SortableTh
                        label="Status"
                        sortKey="isActive"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      />
                      <SortableTh
                        label="Padrão"
                        sortKey="isDefault"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      />
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {visibleRestaurants.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-sm text-gray-500">Nenhum restaurante encontrado com os filtros atuais.</td>
                      </tr>
                    ) : (
                      visibleRestaurants.map((restaurant) => (
                        <tr key={restaurant.id} className="cursor-pointer hover:bg-gray-50" onClick={() => openView(restaurant)}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{restaurant.name}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{restaurant.code || 'N/D'}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{restaurant.timezone}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <StatusPill label={restaurant.isActive ? 'Ativo' : 'Inativo'} variant={restaurant.isActive ? 'success' : 'muted'} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{isDefault(restaurant.id) ? 'Sim' : 'Não'}</td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            {canManageRestaurants && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markDefault(restaurant);
                                }}
                                className="mr-4 text-indigo-600 hover:text-indigo-900 disabled:text-indigo-300"
                                disabled={isDefault(restaurant.id)}
                                title={isDefault(restaurant.id) ? 'Já é padrão' : 'Definir como padrão'}
                              >
                                <CheckCircleIcon className="h-5 w-5" />
                              </button>
                            )}
                            {restaurant.isOperatingHoursEnabled && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openHours(restaurant);
                                }}
                                className="mr-4 text-gray-600 hover:text-gray-900"
                                title="Horários"
                              >
                                <ClockIcon className="h-5 w-5" />
                              </button>
                            )}
                            {canManageRestaurants && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEdit(restaurant);
                                }}
                                className="mr-4 text-indigo-600 hover:text-indigo-900"
                                title="Editar"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            )}
                            {canManageRestaurants && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archive(restaurant);
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Excluir"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {!loading && totalItems > 0 ? (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalItems={totalItems}
                  pageSize={pageSize}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={modalOpen} onClose={closeModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <Dialog.Panel className="mx-auto max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded bg-white p-4 sm:p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">
              {modalMode === 'edit' ? 'Editar restaurante' : 'Novo restaurante'}
            </Dialog.Title>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium leading-6 text-gray-900">Nome *</label>
                  <input type="text" {...register('name')} className="mt-1 block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm" />
                  {errors.name ? <p className="mt-1 text-sm text-red-600">{errors.name.message}</p> : null}
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">Código interno</label>
                  <input type="text" {...register('code')} className="mt-1 block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">Fuso horário</label>
                  <input type="text" {...register('timezone')} className="mt-1 block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm" />
                </div>
              </div>

              <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                {modalMode === 'edit' ? (
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <input type="checkbox" {...register('isActive')} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    Restaurante ativo
                  </label>
                ) : null}
                <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <input type="checkbox" {...register('isOperatingHoursEnabled')} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  Horário de funcionamento habilitado
                </label>
                <p className="text-xs text-gray-600">
                  Com essa opção ativa, o sistema bloqueia chamadas fora do horário no QR Code e nas integrações.
                </p>
              </div>

              <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                <button type="button" onClick={closeModal} className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60">
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={Boolean(viewingRestaurant)} onClose={() => setViewingRestaurant(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <Dialog.Panel className="mx-auto max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded bg-white p-4 sm:p-6 shadow-xl">
            <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4 border-b pb-2">Detalhes do restaurante</Dialog.Title>

            {viewingRestaurant ? (
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Nome</p>
                  <p className="text-base font-medium text-gray-900">{viewingRestaurant.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Código</p>
                  <p>{viewingRestaurant.code || 'N/D'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Fuso horário</p>
                  <p>{viewingRestaurant.timezone}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Status</p>
                  <StatusPill label={viewingRestaurant.isActive ? 'Ativo' : 'Inativo'} variant={viewingRestaurant.isActive ? 'success' : 'muted'} />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Padrão da empresa</p>
                  <p>{isDefault(viewingRestaurant.id) ? 'Sim' : 'Não'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500">Horário de funcionamento</p>
                  <p>{viewingRestaurant.isOperatingHoursEnabled ? 'Habilitado' : 'Desabilitado'}</p>
                </div>

                {viewingRestaurant.isOperatingHoursEnabled ? (
                  <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Agenda semanal</p>

                    {viewHoursLoading ? (
                      <p className="text-sm text-gray-600">Carregando agenda...</p>
                    ) : viewHoursError ? (
                      <p className="text-sm text-red-600">{viewHoursError}</p>
                    ) : groupedViewHours.some((item) => item.shifts.length > 0) ? (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {groupedViewHours.map((item) => (
                          <div key={item.day} className="rounded-md border border-gray-200 bg-white px-3 py-2">
                            <p className="text-xs font-semibold text-gray-700">{item.label}</p>
                            {item.shifts.length ? (
                              <ul className="mt-1 space-y-1 text-xs text-gray-600">
                                {item.shifts.map((shift, index) => (
                                  <li key={`${item.day}-${shift.openTime}-${shift.closeTime}-${index}`}>
                                    {shift.openTime} às {shift.closeTime}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mt-1 text-xs text-gray-400">Sem turno</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Nenhum turno cadastrado.</p>
                    )}

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Exceções (datas)</p>
                      {viewClosureDates.length ? (
                        <ul className="mt-1 space-y-1 text-xs text-gray-600">
                          {viewClosureDates
                            .slice()
                            .sort((a, b) => String(a.date).localeCompare(String(b.date)))
                            .map((closure, index) => (
                              <li key={`${closure.date}-${index}`}>
                                {closure.date} {closure.isClosed ? '- Fechado' : '- Aberto'}
                                {closure.reason ? ` (${closure.reason})` : ''}
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <p className="mt-1 text-xs text-gray-400">Sem exceções cadastradas.</p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4">
              <button type="button" onClick={() => setViewingRestaurant(null)} className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Fechar</button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <OperatingHoursModal
        restaurantId={selectedRestaurantId}
        companyId={config.companyId}
        isOpen={hoursModalOpen}
        onClose={() => setHoursModalOpen(false)}
      />

      <ContextHelpDrawer
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Restaurantes"
        description="Revise cadastro, restaurante padrão, horários de funcionamento e impactos operacionais."
        sections={RESTAURANTS_HELP_SECTIONS}
        activeSectionKey={helpSectionKey}
        onSectionChange={setHelpSectionKey}
      />
    </section>
  );
};

export default RestaurantsPage;
